import React, { Component } from 'react';
import { MobileTruckLoadContext } from './MobileTruckLoadContext';

function effClassLocal(eff) {
    if (eff >= 90) return 'good';
    if (eff >= 75) return 'ok';
    return 'low';
}

// loading_started_at/loading_completed_at are stored with real IST wall-clock
// digits under the UTC label (see markTripLoadingStarted/Completed in
// lmconsigneesModel.js) - subtracting two such timestamps from each other
// (the completed-trip case below) cancels the mislabeling out and is already
// safe as plain millisecond subtraction. Comparing one of them against a
// genuine current-instant Date (the still-running case) is NOT safe the same
// way - "now" has to first be shifted onto that same mislabeled timeline, or
// the +05:30 offset baked into the stored value would throw the elapsed time
// off by 5.5 hours. This is that shift.
var IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
function nowAsStoredIST() {
    return new Date(Date.now() + IST_OFFSET_MS);
}

// Compact form of MobileSummary.jsx's own formatDuration (same drop-zero-parts
// logic, just "2h 15m 25s" instead of "2 hr 15 min 25 sec" to fit a card's
// single timer line) - duplicated locally rather than shared, same as
// effClassLocal already is between the two files.
function formatDurationShort(ms) {
    var totalSeconds = Math.max(0, Math.round(ms / 1000));
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    var parts = [];
    if (hours > 0) parts.push(hours + 'h');
    if (hours > 0 || minutes > 0) parts.push(minutes + 'm');
    parts.push(seconds + 's');
    return parts.join(' ');
}

// Parked for now, per request - flip back on later. The computation (scannerRows)
// is left in place below since it's cheap and self-contained; only the render is gated.
const SHOW_SCANNER_QUALITY = false;

// Real data - fetches lm_truckload_plan_data (expected) joined against
// lm_truckload_scan_log (actual) for the picked date via ctx.fetchDashboardTrips,
// same real-data join the Summary screen already uses for one trip at a time.
export default class MobileDashboard extends Component {
    static contextType = MobileTruckLoadContext;

    constructor(props, context) {
        super(props, context);
        // Finish Loading (from Summary) navigates back here with the just-completed
        // trip's date, so that trip is immediately visible instead of only showing
        // up once the user manually re-picks today's date.
        var passedDate = props.location && props.location.state && props.location.state.date;
        this.state = {
            date: passedDate || new Date().toISOString().slice(0, 10),
            loading: false,
            plannedVehicles: 0,
            totalExpected: 0,
            totalLoaded: 0,
            totalExceptions: 0,
            trips: [],
            planTotals: [],
            statusFilter: 'all',
            selectedPlanId: '',
            // Bumped every second (see componentDidMount) purely to force a
            // re-render - render() reads the real clock itself via
            // nowAsStoredIST(), this is just the trigger for a still-running
            // trip's "Running for..." timer to keep advancing on screen.
            nowTick: 0
        };
        this._lastPortCode = context ? context.portCode : null;
    }

    componentDidMount() {
        // forceRefresh:true - this screen gets remounted every time it's navigated
        // back to (e.g. from Input Plan after starting/resuming a trip), and the
        // whole point of coming back is to see that just happened - a cached
        // response from before it happened would defeat that.
        this.loadDashboard(this.state.date, true);
        this._timerInterval = setInterval(() => {
            this.setState((prev) => ({ nowTick: prev.nowTick + 1 }));
        }, 1000);
    }

    componentWillUnmount() {
        clearInterval(this._timerInterval);
    }

    // A warehouse switch changes what "this date's trips" even means - refetch for
    // the currently picked date under the newly selected port, same pattern
    // MobileInputPlan already uses for its own port-change detection.
    componentDidUpdate() {
        if (this.context && this.context.portCode !== this._lastPortCode) {
            this._lastPortCode = this.context.portCode;
            this.loadDashboard(this.state.date);
        }
    }

    loadDashboard = (dateStr, forceRefresh) => {
        if (!dateStr) {
            this.setState({ loading: false, plannedVehicles: 0, totalExpected: 0, totalLoaded: 0, totalExceptions: 0, trips: [], planTotals: [] });
            return;
        }
        this.setState({ loading: true });
        this.context.fetchDashboardTrips(dateStr, forceRefresh).then((data) => {
            // A date change while this request was in flight already started its own
            // fetch - let that one's result win instead of overwriting it with stale data.
            if (this.state.date !== dateStr) return;
            var trips = data.trips || [];
            // Only one plan for this date - there's nothing to actually filter, so
            // skip offering "All Plans" as a real choice (see render below) and just
            // pre-select the one plan that exists instead.
            // Sourced from planTotals (every planned trip no), not `trips` (only trips
            // with real scan activity or a Start Loading tap) - a plan with nothing but
            // freshly-uploaded plan data has zero entries in `trips` yet, which would
            // otherwise leave selectedPlanId stuck unset even when there's only one plan.
            var distinctPlanIds = [...new Set((data.planTotals || []).map(p => p.planId))].filter(Boolean);
            this.setState({
                loading: false,
                plannedVehicles: data.plannedVehicles || 0,
                totalExpected: data.totalExpected || 0,
                totalLoaded: data.totalLoaded || 0,
                totalExceptions: data.totalExceptions || 0,
                trips: trips,
                planTotals: data.planTotals || [],
                selectedPlanId: distinctPlanIds.length === 1 ? distinctPlanIds[0] : ''
            });
        }).catch((e) => {
            console.log(e);
            if (this.state.date !== dateStr) return;
            this.setState({ loading: false, plannedVehicles: 0, totalExpected: 0, totalLoaded: 0, totalExceptions: 0, trips: [], planTotals: [] });
        });
    };

    onDateChange = (e) => {
        var dateStr = e.target.value;
        // A plan id valid for the old date may not even exist on the new one -
        // reset back to "All" rather than silently filtering to nothing.
        this.setState({ date: dateStr, selectedPlanId: '' });
        // forceRefresh:true - picking a date is a deliberate "show me what's
        // true for this date right now" action, not a passive remount. Without
        // this, returning to a date already viewed earlier this session (the
        // dashboardCache in MobileTruckLoadContext is keyed by date+port) would
        // silently show that earlier cached snapshot instead of a fresh fetch,
        // missing anything that happened since.
        this.loadDashboard(dateStr, true);
    };

    openHistoricalSummary = (trip) => {
        this.props.history.push({
            // planId/tripNo in the path (not just router state) so a real refresh
            // on Summary can rebuild itself via resumeSessionFromIds instead of
            // losing everything - see that method's own comment.
            pathname: `/lm-mobile/summary/${trip.planId}/${trip.tripNo}`,
            state: {
                mode: 'historical',
                breadcrumbLabel: 'Summary',
                vehicle: trip.vehicle,
                date: trip.dateLabel,
                planId: trip.planId,
                loadType: trip.loadType,
                tripNo: trip.tripNo,
                lsp: trip.lsp,
                dockNumber: trip.dockNumber,
                scannerName: trip.scannerName,
                expectedTotal: trip.expected,
                scannedTotal: trip.actualLoaded,
                remainingTotal: trip.expected - trip.actualLoaded,
                autoScans: trip.autoScans || 0,
                manualScans: trip.manualScans || 0,
                duplicateTotal: trip.duplicate,
                wrongTruckTotal: trip.wrongTruck,
                notFoundTotal: trip.notFound,
                consigneeRows: trip.consignees || [],
                returnScreen: 'dashboard',
                trip: trip
            }
        });
    };

    // Drops straight into Scan exactly where this trip left off, instead of only
    // offering the read-only Summary view for a trip that isn't actually done yet.
    // expectedHUs isn't on the dashboard trip object (that only has the
    // consignee-level rollup) - fetch this date's plan rows the same way Input Plan
    // does and filter to this one trip, then rebuild progress from the real scan log.
    // A pending trip stays resumable through 2 days after its own plan date -
    // same "+2 days" grace window as the desktop's Upload Loading Plan button
    // (isUploadWindowOpen). Parsed with local date getters, not
    // new Date("YYYY-MM-DD") (which is UTC-midnight), so this lines up with
    // the local calendar date the <input type="date"> field actually shows.
    isResumeWindowOpen = (dateStr) => {
        if (!dateStr) return false;
        var parts = dateStr.split('-').map(Number);
        var viewed = new Date(parts[0], parts[1] - 1, parts[2]);
        if (isNaN(viewed.getTime())) return false;
        var deadline = new Date(viewed.getFullYear(), viewed.getMonth(), viewed.getDate() + 2, 23, 59, 59, 999);
        return new Date() <= deadline;
    };

    // Drops into Input Plan instead of straight into Scan - dock/scanner can
    // legitimately change mid-loading (a different dock or handheld scanner
    // takes over), so every resume path funnels through the one screen where
    // those two are actually editable, instead of re-entering Scan with
    // whatever was set the first time silently still in force. Just a plain
    // navigation with the three ids Input Plan's own applyResumeFrom needs -
    // it re-derives everything else (trip status lookup, locking,
    // expectedHUs/scanRows) from those, the same fetches this method used to
    // do itself before pushing straight to Scan.
    resumeFromDashboard = (trip) => {
        this.props.history.push({
            pathname: '/lm-mobile/input-plan',
            state: { resumeFrom: { loadDate: this.state.date, planId: trip.planId, tripNo: trip.tripNo } }
        });
    };

    render() {
        var dateStr = this.state.date;
        var trips = this.state.trips;
        var selectedPlanId = this.state.selectedPlanId;
        // Resume stays available through 2 days after the viewed date, same
        // grace window as the desktop's Upload Loading Plan button.
        var isResumeWindowOpen = this.isResumeWindowOpen(dateStr);
        // Re-read every render (this.state.nowTick ticking every second - see
        // componentDidMount - is what actually triggers that) so every still-
        // running trip's "Running for..." timer keeps advancing live.
        var nowIST = nowAsStoredIST();

        // Sourced from planTotals, not `trips` - see the comment on distinctPlanIds in
        // loadDashboard above for why `trips` alone can't be trusted for this (a plan
        // with only uploaded, not-yet-started plan data has zero entries in `trips`,
        // which would otherwise make its dropdown option - and its whole card - vanish
        // even though plannedVehicles/totalExpected already show it exists).
        var planIdOptions = [...new Set((this.state.planTotals || []).map(p => p.planId))].filter(Boolean).sort();
        // loaded/exceptions/partial-fully-loaded are safe to recompute from the
        // plan-filtered cards - a plan_data-only trip no with zero activity
        // contributes 0 to all of them either way, so omitting it changes nothing.
        // plannedVehicles/totalExpected are NOT safe to derive from `trips` though -
        // most plan_data-only trip nos (zero scans, never started) never get a card
        // at all, so filtering `trips` by plan_id silently undercounts planned/
        // expected for any plan that has one. Those two come from the backend's
        // planTotals instead, which is computed from every planned trip no.
        var planFilteredTrips = selectedPlanId ? trips.filter(t => t.planId === selectedPlanId) : trips;
        var selectedPlanTotals = selectedPlanId ? (this.state.planTotals || []).find(p => p.planId === selectedPlanId) : null;

        var totalExpected = selectedPlanId ? (selectedPlanTotals ? selectedPlanTotals.totalExpected : 0) : this.state.totalExpected;
        var totalLoaded = selectedPlanId ? planFilteredTrips.reduce((s, t) => s + t.actualLoaded, 0) : this.state.totalLoaded;
        var totalExceptions = selectedPlanId ? planFilteredTrips.reduce((s, t) => s + t.duplicate + t.wrongTruck + t.notFound, 0) : this.state.totalExceptions;
        var plannedVehicles = selectedPlanId ? (selectedPlanTotals ? selectedPlanTotals.plannedVehicles : 0) : this.state.plannedVehicles;

        // pendingCount/completedCount are the single source of truth for "is
        // this trip pending or completed" - used by both the top summary tile
        // and the All/Pending/Completed tabs below, so the two can never
        // disagree again. They used to be computed separately: the tile only
        // counted trips with actual scan activity already logged (actualLoaded
        // or any exception > 0), while the tabs counted every not-yet-completed
        // trip regardless of activity - a trip that was started but hadn't had
        // its first scan yet showed up in the "Pending" tab but not in the
        // tile's count, producing exactly the "7 pending in the tab, 6 in the
        // tile" mismatch this was built to prevent.
        var pendingCount = planFilteredTrips.filter(t => !t.completed).length;
        var completedTrips = planFilteredTrips.filter(t => t.completed);
        var completedCount = completedTrips.length;
        // "Efficiency" only means anything for trips that are actually done - a
        // running trip's total scan-attempt count isn't final yet, so mixing it in
        // would understate/overstate the number against trips that aren't finished.
        // Weighted (total loaded / total attempts across completed trips), not a
        // plain average of percentages, so a big trip doesn't get diluted by a small
        // one's % the same way a simple average would.
        var completedScans = completedTrips.reduce((s, t) => s + t.actualLoaded + t.duplicate + t.wrongTruck + t.notFound, 0);
        var completedLoaded = completedTrips.reduce((s, t) => s + t.actualLoaded, 0);
        var avgEff = completedScans > 0 ? Math.round((completedLoaded / completedScans) * 100) : null;

        // Scanner-level quality signal - a scanner (person/device) working multiple
        // vehicles across the day has no rollup anywhere else; each trip card is
        // per-vehicle, not per-scanner. Sourced entirely from completedTrips already
        // in state, no extra fetch. Worst-first so a problem scanner surfaces
        // immediately instead of needing to scan the whole list.
        var scannerTotals = {};
        completedTrips.forEach((t) => {
            var key = t.scannerName || 'Unknown';
            if (!scannerTotals[key]) scannerTotals[key] = { scanner: key, trips: 0, loaded: 0, attempts: 0, exceptions: 0 };
            var s = scannerTotals[key];
            s.trips += 1;
            s.loaded += t.actualLoaded;
            s.attempts += t.actualLoaded + t.duplicate + t.wrongTruck + t.notFound;
            s.exceptions += t.duplicate + t.wrongTruck + t.notFound;
        });
        var scannerRows = Object.values(scannerTotals)
            .map((s) => ({ ...s, efficiency: s.attempts > 0 ? Math.round((s.loaded / s.attempts) * 100) : 0 }))
            .sort((a, b) => a.efficiency - b.efficiency);

        var visibleTrips = planFilteredTrips.filter(t => {
            if (this.state.statusFilter === 'completed') return t.completed;
            if (this.state.statusFilter === 'pending') return !t.completed;
            return true;
        });

        return (
            <div className="mtl-screen-scroll">
                <div className="mtl-breadcrumb"><span className="mtl-crumb-parent">Truck Load Automation</span><span className="mtl-crumb-sep">&rsaquo;</span><b>Dashboard</b></div>
                        <div className="mtl-dash-body">
                            <div className="mtl-filter-panel">
                                <div className="mtl-form-grid">
                                    <div className="mtl-form-group">
                                        <label className="mtl-dash-label">Select Order Date</label>
                                        <input className="mtl-field" type="date" value={dateStr} onChange={this.onDateChange} style={{ marginBottom: 0 }} />
                                    </div>
                                    {dateStr && planIdOptions.length > 0 && (
                                        <div className="mtl-form-group">
                                            <label className="mtl-dash-label">Plan Id</label>
                                            <select className="mtl-field" value={selectedPlanId} onChange={(e) => this.setState({ selectedPlanId: e.target.value })} style={{ marginBottom: 0 }}>
                                                {planIdOptions.length > 1 && <option value="">All Plans</option>}
                                                {planIdOptions.map(id => <option key={id} value={id}>{id}</option>)}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {!dateStr ? (
                                <div className="mtl-notice-block">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>
                                    </svg>
                                    <h3>Pick a date</h3>
                                    <p>Select a date above to see that day's loading trips and efficiency.</p>
                                </div>
                            ) : this.state.loading ? (
                                <div className="mtl-notice-block">
                                    <p>Loading...</p>
                                </div>
                            ) : (
                                <div>
                                    <div className="mtl-dash-counters">
                                        <div className="mtl-dash-counter mtl-accent-blue">
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{plannedVehicles}</div>
                                                <div className="mtl-dash-counter-label">Planned Vehicles</div>
                                            </div>
                                        </div>
                                        <div className="mtl-dash-counter mtl-accent-green">
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{pendingCount} / {completedCount}</div>
                                                <div className="mtl-dash-counter-label">Pending / Completed</div>
                                            </div>
                                        </div>
                                        <div className="mtl-dash-counter mtl-accent-blue">
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8L12 3 3 8v8l9 5 9-5V8z"></path><path d="M3 8l9 5 9-5"></path><path d="M12 13v9"></path></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{totalExpected}</div>
                                                <div className="mtl-dash-counter-label">Total Expected HU's</div>
                                            </div>
                                        </div>
                                        <div className="mtl-dash-counter mtl-accent-green">
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8L12 3 3 8v8l9 5 9-5V8z"></path><path d="M3 8l9 5 9-5"></path><path d="M12 13v9"></path></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{totalLoaded}</div>
                                                <div className="mtl-dash-counter-label">Total Loaded HU's</div>
                                            </div>
                                        </div>
                                        <div className="mtl-dash-counter mtl-accent-orange">
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{totalExceptions}</div>
                                                <div className="mtl-dash-counter-label">Exceptions</div>
                                            </div>
                                        </div>
                                        <div className={avgEff === null ? "mtl-dash-counter" : "mtl-dash-counter mtl-accent-" + effClassLocal(avgEff)}>
                                            <span className="mtl-dash-counter-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"></path><path d="M18.7 8l-5.7 5.7-4-4L3.6 15"></path></svg>
                                            </span>
                                            <div className="mtl-dash-counter-info">
                                                <div className="mtl-dash-counter-value">{avgEff === null ? '—' : avgEff + '%'}</div>
                                                <div className="mtl-dash-counter-label">Average Efficiency</div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mtl-dash-trips-heading">Loaded Vehicle Metrics</p>
                                    <div className="mtl-status-tabs">
                                        {[
                                            { key: 'all', label: 'All', count: trips.length },
                                            { key: 'pending', label: 'Pending', count: pendingCount },
                                            { key: 'completed', label: 'Completed', count: completedCount }
                                        ].map((tab) => (
                                            <button
                                                key={tab.key}
                                                type="button"
                                                className={"mtl-status-tab" + (this.state.statusFilter === tab.key ? " mtl-active" : "")}
                                                onClick={() => this.setState({ statusFilter: tab.key })}
                                            >{tab.label} ({tab.count})</button>
                                        ))}
                                    </div>
                                    <div className="mtl-trips-list">
                                        {visibleTrips.length === 0 && (
                                            <div className="mtl-notice-block">
                                                <p>{trips.length === 0 ? 'No vehicles loaded yet for this date.' : 'No trips match this filter.'}</p>
                                            </div>
                                        )}
                                        {visibleTrips.map((t, i) => {
                                            // Efficiency (accuracy) only exists once a trip is completed - t.efficiency
                                            // is null while running, so color/badge both fall back to progress instead.
                                            var badgeValue = t.completed ? t.efficiency : t.progress;
                                            var cls = effClassLocal(badgeValue);
                                            var totalExc = t.duplicate + t.wrongTruck + t.notFound;
                                            // Pending -> live "how long has this been running" since Start
                                            // Loading was tapped, ticking up every second (nowIST, above).
                                            // Completed -> the fixed duration it actually took, computed once
                                            // (loading_completed_at - loading_started_at never changes again).
                                            // Neither shows if startedAt itself is missing (a trip predating
                                            // this field, or a data issue) rather than displaying a bogus timer.
                                            // Folded straight into the Pending/Completed badge itself (not a
                                            // separate card row) - just the duration text, the badge's own
                                            // "Pending"/"Completed" word already says which case it is.
                                            var timerLabel = null;
                                            if (t.startedAt) {
                                                if (t.completed && t.completedAt) {
                                                    timerLabel = formatDurationShort(new Date(t.completedAt) - new Date(t.startedAt));
                                                } else if (!t.completed) {
                                                    timerLabel = formatDurationShort(nowIST - new Date(t.startedAt));
                                                }
                                            }
                                            return (
                                                <div className={"mtl-trip-card mtl-" + cls} key={i}>
                                                    <div className="mtl-trip-card-head">
                                                        <div className="mtl-trip-vehicle-wrap">
                                                            <span className="mtl-trip-vehicle-icon">
                                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="7" width="14" height="10" rx="1"></rect><path d="M15 10h4l3 3v4h-7z"></path><circle cx="6" cy="18.5" r="1.7"></circle><circle cx="17.5" cy="18.5" r="1.7"></circle></svg>
                                                            </span>
                                                            <div className="mtl-trip-vehicle-stack">
                                                                <span className="mtl-trip-vehicle">{t.vehicle}</span>
                                                                {/* Status badge moved down here, alongside the trip no, instead of
                                                                    sharing the vehicle number's own row - competing with a fixed-
                                                                    width pill (plus the eff-badge/Resume/View buttons on the other
                                                                    side) was leaving too little room, so the vehicle number itself
                                                                    kept ellipsizing on pending cards. */}
                                                                <span className="mtl-trip-subtitle-row">
                                                                    <span className="mtl-trip-subtitle">Trip {t.tripNo}</span>
                                                                    <span className={"mtl-trip-status-badge " + (t.completed ? "mtl-completed" : "mtl-pending")}>{t.completed ? 'Completed' : 'Pending'}</span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="mtl-trip-card-head-right">
                                                            <span className={"mtl-trip-eff-badge mtl-" + cls}>{badgeValue}% {t.completed ? 'Eff' : 'Progress'}</span>
                                                            {isResumeWindowOpen && !t.completed && (
                                                                <button className="mtl-trip-eye-btn" type="button" aria-label="Resume Loading" onClick={() => this.resumeFromDashboard(t)}>
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                                                </button>
                                                            )}
                                                            <button className="mtl-trip-eye-btn" type="button" aria-label="View Summary" onClick={() => this.openHistoricalSummary(t)}>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {(t.dockNumber || timerLabel) && (
                                                        // Current dock + running/completed timer, as two separate
                                                        // chips rather than one joined string - sole home for the
                                                        // timer now (dropped back out of the Pending/Completed badge
                                                        // above, which now just says Pending/Completed again).
                                                        <div className="mtl-trip-meta-row">
                                                            {t.dockNumber && (
                                                                <span className="mtl-trip-meta-chip">
                                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                                                                    {t.dockNumber}
                                                                </span>
                                                            )}
                                                            {timerLabel && (
                                                                <span className={"mtl-trip-meta-chip" + (t.completed ? "" : " mtl-timer-live")}>
                                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                                                    {timerLabel}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Same tile style as Summary's Load Progress/Consignee-wise
                                                        Metrics grids, instead of this card's own divider-row style. */}
                                                    <div className="mtl-progress-stats-grid mtl-progress-stats-grid-3">
                                                        <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{t.expected}</span><span className="mtl-progress-stat-lbl">Expected</span></div>
                                                        <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{t.actualLoaded}</span><span className="mtl-progress-stat-lbl">Loaded</span></div>
                                                        <div className="mtl-progress-stat"><span className="mtl-progress-stat-val">{totalExc}</span><span className="mtl-progress-stat-lbl">Exceptions</span></div>
                                                    </div>
                                                    <div className="mtl-trip-flags">
                                                        <span className={"mtl-trip-flag " + (t.duplicate > 0 ? 'mtl-orange' : 'mtl-neutral')}>{t.duplicate} Duplicate</span>
                                                        <span className={"mtl-trip-flag " + (t.wrongTruck > 0 ? 'mtl-red' : 'mtl-neutral')}>{t.wrongTruck} Wrong Truck</span>
                                                        <span className={"mtl-trip-flag " + (t.notFound > 0 ? 'mtl-purple' : 'mtl-neutral')}>{t.notFound} Not Found</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {SHOW_SCANNER_QUALITY && scannerRows.length > 0 && (
                                        <>
                                            <p className="mtl-dash-trips-heading">Scanner Quality (Completed Trips)</p>
                                            <div className="mtl-trips-list">
                                                {scannerRows.map((s, i) => {
                                                    var cls = effClassLocal(s.efficiency);
                                                    return (
                                                        <div className={"mtl-trip-card mtl-" + cls} key={i}>
                                                            <div className="mtl-trip-card-head">
                                                                <div className="mtl-trip-vehicle-wrap">
                                                                    <span className="mtl-trip-vehicle">{s.scanner}</span>
                                                                    <span className="mtl-scanner-trip-count">{s.trips} trip{s.trips > 1 ? 's' : ''}</span>
                                                                </div>
                                                                <span className={"mtl-trip-eff-badge mtl-" + cls}>{s.efficiency}% Eff</span>
                                                            </div>
                                                            <div className="mtl-trip-main-stats">
                                                                <div className="mtl-trip-main-stat"><span className="mtl-trip-main-val">{s.loaded}</span><span className="mtl-trip-main-lbl">Loaded</span></div>
                                                                <div className="mtl-trip-main-divider"></div>
                                                                <div className="mtl-trip-main-stat"><span className="mtl-trip-main-val">{s.attempts}</span><span className="mtl-trip-main-lbl">Attempts</span></div>
                                                                <div className="mtl-trip-main-divider"></div>
                                                                <div className="mtl-trip-main-stat"><span className="mtl-trip-main-val">{s.exceptions}</span><span className="mtl-trip-main-lbl">Exceptions</span></div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
            </div>
        );
    }
}
